(() => {
  const PREVIEW_WIDTH = 2048;
  const PREVIEW_HEIGHT = 1140;
  const TK_VERSION = "TKV2";
  const TK_LAYOUT_PROFILE = "treekey_2048x1140_v3";

  const FONT_STACKS = {
    Arial: "Arial, sans-serif",
    "Times New Roman": "'Times New Roman', serif",
    "Courier New": "'Courier New', monospace",
    Caveat: "'Caveat', cursive",
    Impact: "Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    Tangerine: "'Tangerine', cursive",
    Aldrich: "'Aldrich', sans-serif",
    Audiowide: "'Audiowide', sans-serif"
  };

  const FONT_WEIGHTS = {
    Arial: 600,
    "Times New Roman": 600,
    "Courier New": 600,
    Caveat: 700,
    Impact: 400,
    Tangerine: 700,
    Aldrich: 400,
    Audiowide: 400
  };

  const CUSTOM_FONT_LOADS = {
    Caveat: '700 32px "Caveat"',
    Tangerine: '700 32px "Tangerine"',
    Aldrich: '400 32px "Aldrich"',
    Audiowide: '400 32px "Audiowide"'
  };

  const SURFACE_LAYOUTS = {
    front_left: {
      maxChars: 28,
      defaultFontSize: 40,
      safeBox: { x: 51, y: 468, w: 1948, h: 204 },
      clipPath:
        "M 51 468 L 51 619 L 97 672 L 1660 672 L 1841 637 L 1999 628 L 1998 468 L 1895 468 L 1883 508 L 1847 533 L 1802 520 L 1779 468 Z"
    },
    front_right: {
      maxChars: 28,
      defaultFontSize: 40,
      safeBox: { x: 51, y: 468, w: 1948, h: 204 },
      clipPath:
        "M 51 521 L 51 672 L 1779 672 L 1795 627 L 1836 606 L 1880 628 L 1895 672 L 1999 671 L 1999 512 L 1828 501 L 1661 468 L 97 468 Z"
    },
    back: {
      maxChars: 40,
      defaultFontSize: 40,
      safeBox: { x: 718, y: 393, w: 1281, h: 355 },
      clipPath:
        "M 718 570 L 923 747 L 1662 747 L 1823 719 L 1998 709 L 1998 431 L 1826 421 L 1663 393 L 923 393 Z"
    }
  };

  let measureCanvas = null;

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-tk-configurator]").forEach(initConfigurator);
  });

  function initConfigurator(root) {
    const rawConfig = root.querySelector("[data-tk-config]");
    if (!rawConfig) return;

    let config;
    try {
      config = JSON.parse(rawConfig.textContent);
    } catch (error) {
      console.error("Treekey-Konfigurator: config JSON ungültig.", error);
      return;
    }

    const state = buildInitialState();

    const propInputs = {};
    root.querySelectorAll("[data-prop]").forEach((input) => {
      propInputs[input.dataset.prop] = input;
    });

    const elements = {
      root,
      tabs: [...root.querySelectorAll("[data-surface-tab]")],
      previewStages: [...root.querySelectorAll("[data-preview-stage]")],
      activeSurfaceLabel: root.querySelector("[data-active-surface-label]"),
      text: root.querySelector('[data-field="text"]'),
      font: root.querySelector('[data-field="font"]'),
      textStyleButtons: [...root.querySelectorAll("[data-text-style]")],
      rotation: root.querySelector('[data-field="rotation"]'),
      rotationNumber: root.querySelector('[data-field="rotationNumber"]'),
      fontSize: root.querySelector('[data-field="fontSize"]'),
      textX: root.querySelector('[data-field="textX"]'),
      textY: root.querySelector('[data-field="textY"]'),
      artX: root.querySelector('[data-field="artX"]'),
      artY: root.querySelector('[data-field="artY"]'),
      artScale: root.querySelector('[data-field="artScale"]'),
      artRotation: root.querySelector('[data-field="artRotation"]'),
      artRotationNumber: root.querySelector('[data-field="artRotationNumber"]'),
      outputs: {
        fontSize: root.querySelector('[data-output="fontSize"]'),
        textX: root.querySelector('[data-output="textX"]'),
        textY: root.querySelector('[data-output="textY"]'),
        artX: root.querySelector('[data-output="artX"]'),
        artY: root.querySelector('[data-output="artY"]'),
        artScale: root.querySelector('[data-output="artScale"]')
      },
      alignButtons: [...root.querySelectorAll("[data-align]")],
      artTypeButtons: [...root.querySelectorAll("[data-art-type]")],
      iconPickerWrap: root.querySelector("[data-icon-picker-wrap]"),
      iconPicker: root.querySelector("[data-icon-picker]"),
      uploadGroups: [...root.querySelectorAll("[data-upload-group]")],
      uploadInputs: [...root.querySelectorAll("[data-upload-input]")],
      uploadMeta: [...root.querySelectorAll("[data-upload-meta]")],
      clearArtBtn: root.querySelector("[data-clear-art]"),
      summary: root.querySelector("[data-summary]"),
      form: root.querySelector(".tkcfg__form"),
      submit: root.querySelector(".tkcfg__submit"),
      variantSelect: root.querySelector("[data-variant-select]"),
      configPriceNote: root.querySelector("[data-config-price-note]"),
      propInputs,
      admin: null
    };

    renderIconPicker(config, elements, state);
    bindEvents(root, config, state, elements);
    initAdminMode(root, config, state, elements);
    render(root, config, state, elements);

    preloadConfiguredFonts(config).then(() => {
      render(root, config, state, elements);
    });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        render(root, config, state, elements);
      });
    }
  }

  function t(config, key, fallback = "") {
    return config?.ui?.[key] ?? fallback;
  }

  function buildInitialState() {
    return {
      activeSurface: "front_left",
      activePositionTarget: "text",
      surfaces: {
        front_left: buildSurfaceState("front_left"),
        front_right: buildSurfaceState("front_right"),
        back: buildSurfaceState("back")
      }
    };
  }

  function buildSurfaceState(surfaceKey) {
    const layout = SURFACE_LAYOUTS[surfaceKey];
    return {
      text: "",
      font: "Arial",
      textStyle: "filled",
      fontSize: layout.defaultFontSize || 40,
      align: "center",
      rotation: 0,
      textX: 50,
      textY: 50,
      artType: "NONE",
      artId: "",
      artName: "",
      artData: "",
      artX: 50,
      artY: 50,
      artScale: 80,
      artRotation: 0,
      artNaturalWidth: 1,
      artNaturalHeight: 1
    };
  }

  function bindEvents(root, config, state, elements) {
    elements.tabs.forEach((button) => {
      button.addEventListener("click", () => {
        state.activeSurface = button.dataset.surfaceTab;
        render(root, config, state, elements);
      });
    });

    elements.text?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).text = event.target.value;
      render(root, config, state, elements);
    });

    elements.font?.addEventListener("change", async (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).font = event.target.value;
      await ensureFontLoaded(event.target.value);
      render(root, config, state, elements);
    });

    elements.textStyleButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.activePositionTarget = "text";
        getActiveSurfaceState(state).textStyle = button.dataset.textStyle;
        render(root, config, state, elements);
      });
    });

    elements.rotation?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).rotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.rotationNumber?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).rotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.rotationNumber?.addEventListener("change", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).rotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.fontSize?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).fontSize = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.textX?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).textX = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.textY?.addEventListener("input", (event) => {
      state.activePositionTarget = "text";
      getActiveSurfaceState(state).textY = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.artX?.addEventListener("input", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artX = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.artY?.addEventListener("input", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artY = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.artScale?.addEventListener("input", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artScale = Number(event.target.value);
      render(root, config, state, elements);
    });

    elements.artRotation?.addEventListener("input", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artRotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.artRotationNumber?.addEventListener("input", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artRotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.artRotationNumber?.addEventListener("change", (event) => {
      state.activePositionTarget = "art";
      getActiveSurfaceState(state).artRotation = normalizeRotationValue(event.target.value);
      render(root, config, state, elements);
    });

    elements.alignButtons.forEach((button) => {
      button.addEventListener("click", () => {
        state.activePositionTarget = "text";
        const surface = getActiveSurfaceState(state);
        const align = button.dataset.align;
        surface.align = align;
        surface.textX = align === "left" ? 0 : align === "right" ? 100 : 50;
        render(root, config, state, elements);
      });
    });

    elements.artTypeButtons.forEach((button) => {
      button.addEventListener("click", async () => {
        const surfaceKey = state.activeSurface;
        const surface = getActiveSurfaceState(state);
        const nextType = button.dataset.artType || "NONE";

        surface.artType = nextType;

        if (nextType === "NONE") {
          clearSurfaceArt(surface);
          clearSurfaceUploadInput(root, surfaceKey);
          state.activePositionTarget = "text";
          render(root, config, state, elements);
          return;
        }

        if (nextType === "ICON") {
          clearSurfaceUploadInput(root, surfaceKey);
          surface.artData = "";
          state.activePositionTarget = "art";

          if (!surface.artId) {
            const first = getThemeIconLibrary(config)[0];
            if (first) {
              surface.artId = first.id;
              surface.artName = first.label;
              surface.artScale = 80;
              const size = await getImageNaturalSize(first.image);
              surface.artNaturalWidth = size.width;
              surface.artNaturalHeight = size.height;
            } else {
              surface.artId = "";
              surface.artName = "";
              surface.artNaturalWidth = 1;
              surface.artNaturalHeight = 1;
            }
          } else {
            const currentIcon = getThemeIconById(surface.artId, config);
            if (currentIcon) {
              const size = await getImageNaturalSize(currentIcon.image);
              surface.artNaturalWidth = size.width;
              surface.artNaturalHeight = size.height;
            }
          }

          render(root, config, state, elements);
          return;
        }

        if (nextType === "UPLOAD") {
          state.activePositionTarget = "art";
          surface.artId = "";
          surface.artName = surface.artName || "";
          surface.artScale = 80;
          surface.artNaturalWidth = 1;
          surface.artNaturalHeight = 1;
          render(root, config, state, elements);
        }
      });
    });

    if (elements.iconPicker) {
      elements.iconPicker.addEventListener("click", async (event) => {
        const button = event.target.closest("[data-icon-id]");
        if (!button) return;

        const icon = getThemeIconById(button.dataset.iconId, config);
        if (!icon) return;

        const surfaceKey = state.activeSurface;
        const surface = getActiveSurfaceState(state);

        surface.artType = "ICON";
        surface.artId = icon.id;
        surface.artName = icon.label;
        surface.artData = "";
        surface.artScale = 80;

        clearSurfaceUploadInput(root, surfaceKey);

        const size = await getImageNaturalSize(icon.image);
        surface.artNaturalWidth = size.width;
        surface.artNaturalHeight = size.height;

        state.activePositionTarget = "art";
        render(root, config, state, elements);
      });
    }

    elements.uploadInputs.forEach((input) => {
      input.addEventListener("change", async (event) => {
        const surfaceKey = input.dataset.uploadInput;
        const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
        const surface = state.surfaces[surfaceKey];

        if (!file) {
          clearSurfaceArt(surface);
          state.activePositionTarget = "text";
          render(root, config, state, elements);
          return;
        }

        if (!isAllowedUploadType(file)) {
          alert(t(config, "uploadInvalidType", "Bitte lade nur SVG- oder PNG-Dateien hoch."));
          clearNativeFileInput(input);
          clearSurfaceArt(surface);
          state.activePositionTarget = "text";
          render(root, config, state, elements);
          return;
        }

        if (file.size > 4 * 1024 * 1024) {
          alert(t(config, "uploadFileTooLarge", "Die Datei ist zu groß. Bitte maximal 4 MB."));
          clearNativeFileInput(input);
          clearSurfaceArt(surface);
          state.activePositionTarget = "text";
          render(root, config, state, elements);
          return;
        }

        const dataUrl = await readFileAsDataUrl(file);
        const size = await getImageNaturalSize(dataUrl);

        surface.artType = "UPLOAD";
        surface.artId = "";
        surface.artName = file.name;
        surface.artData = dataUrl;
        surface.artNaturalWidth = size.width;
        surface.artNaturalHeight = size.height;
        surface.artScale = 80;

        state.activePositionTarget = "art";
        render(root, config, state, elements);
      });
    });

    elements.clearArtBtn?.addEventListener("click", () => {
      const surfaceKey = state.activeSurface;
      const surface = state.surfaces[surfaceKey];

      clearSurfaceArt(surface);
      clearSurfaceUploadInput(root, surfaceKey);
      state.activePositionTarget = "text";
      render(root, config, state, elements);
    });

    elements.previewStages.forEach((stage) => {
      stage.addEventListener("click", (event) => {
        const activeSurfaceKey = state.activeSurface;
        const layout = SURFACE_LAYOUTS[activeSurfaceKey];
        const active = state.surfaces[activeSurfaceKey];

        const targetType =
          state.activePositionTarget === "art" && active.artType !== "NONE"
            ? "art"
            : "text";

        const svg = stage.querySelector("svg");
        if (!svg) return;

        const rect = svg.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * PREVIEW_WIDTH;
        const y = ((event.clientY - rect.top) / rect.height) * PREVIEW_HEIGHT;

        const safe = layout.safeBox;
        const normalizedX = clamp((x - safe.x) / safe.w, 0, 1);
        const normalizedY = clamp((y - safe.y) / safe.h, 0, 1);

        if (targetType === "art") {
          active.artX = Math.round(normalizedX * 100);
          active.artY = Math.round(normalizedY * 100);
        } else {
          active.textX = Math.round(normalizedX * 100);
          active.textY = Math.round(normalizedY * 100);
        }

        render(root, config, state, elements);
      });
    });
  }

  function initAdminMode(root, config, state, elements) {
    const params = new URLSearchParams(window.location.search);
    const isAdminMode = params.get("admin") === "1";

    if (!isAdminMode || !elements.form) return;

    const adminCard = document.createElement("div");
    adminCard.className = "tkcfg__card tkcfg__admin-card";
    adminCard.innerHTML = `
      <div class="tkcfg__card-head">
        <h3 class="tkcfg__card-title">${escapeHtml(t(config, "adminTitle", "Interner Restore-Modus"))}</h3>
        <span class="tkcfg__card-meta">${escapeHtml(t(config, "adminMeta", "gleiche PDP · gleicher Stand"))}</span>
      </div>

      <label class="tkcfg__label" for="tkcfg-admin-code">${escapeHtml(t(config, "adminCodeLabel", "_tk_code einfügen"))}</label>
      <textarea
        id="tkcfg-admin-code"
        class="tkcfg__input tkcfg__admin-textarea"
        rows="6"
        placeholder="${escapeHtml(t(config, "adminCodePlaceholder", "TKV2|FL[...]|FR[...]|BK[...]"))}"
      ></textarea>

      <div class="tkcfg__admin-actions">
        <button type="button" class="tkcfg__quick-btn" data-admin-load>${escapeHtml(t(config, "adminLoad", "Konfiguration laden"))}</button>
        <button type="button" class="tkcfg__quick-btn" data-admin-copy>${escapeHtml(t(config, "adminCopy", "Aktuellen _tk_code kopieren"))}</button>
      </div>

      <div class="tkcfg__admin-status" data-admin-status></div>
    `;

    elements.form.insertBefore(adminCard, elements.form.firstChild);

    const codeInput = adminCard.querySelector("#tkcfg-admin-code");
    const loadBtn = adminCard.querySelector("[data-admin-load]");
    const copyBtn = adminCard.querySelector("[data-admin-copy]");
    const statusEl = adminCard.querySelector("[data-admin-status]");

    elements.admin = {
      card: adminCard,
      codeInput,
      loadBtn,
      copyBtn,
      statusEl
    };

    const presetCode = params.get("tk");
    if (presetCode) {
      codeInput.value = presetCode;
    }

    loadBtn.addEventListener("click", async () => {
      const raw = codeInput.value.trim();
      if (!raw) {
        setAdminStatus(elements, t(config, "adminStatusMissing", "Bitte zuerst einen _tk_code einfügen."));
        return;
      }

      try {
        importTkCode(raw, state);
        await hydrateArtNaturalSizes(state, config);
        render(root, config, state, elements);
        setAdminStatus(elements, t(config, "adminStatusLoaded", "Konfiguration geladen."));
      } catch (error) {
        console.error(error);
        setAdminStatus(elements, t(config, "adminStatusInvalid", "Code konnte nicht geladen werden. Format prüfen."));
      }
    });

    copyBtn.addEventListener("click", async () => {
      const currentCode = elements.propInputs["tk-code"]?.value || "";
      if (!currentCode) {
        setAdminStatus(elements, t(config, "adminStatusNoCode", "Kein _tk_code vorhanden."));
        return;
      }

      try {
        await navigator.clipboard.writeText(currentCode);
        setAdminStatus(elements, t(config, "adminStatusCopied", "Aktueller _tk_code kopiert."));
      } catch (error) {
        console.error(error);
        setAdminStatus(elements, t(config, "adminStatusCopyFailed", "Kopieren nicht möglich. Bitte manuell kopieren."));
      }
    });
  }

  function render(root, config, state, elements) {
    enforceAllTextBounds(state);
    enforceAllArtBounds(state);
    renderTabs(config, state, elements);
    renderControls(state, elements);
    renderArtControls(config, state, elements);
    renderUploadGroups(config, state, elements);
    renderPreview(config, state, elements);
    renderSummary(config, state, elements);
    syncProperties(config, state, elements);
    applyConfiguredVariant(root, config, state, elements);
  }

  function renderTabs(config, state, elements) {
    elements.tabs.forEach((button) => {
      const isActive = button.dataset.surfaceTab === state.activeSurface;
      button.classList.toggle("is-active", isActive);
      button.setAttribute("aria-selected", String(isActive));
    });

    if (elements.activeSurfaceLabel) {
      elements.activeSurfaceLabel.textContent = config.surfaces[state.activeSurface].label;
    }
  }

  function renderControls(state, elements) {
    const surfaceKey = state.activeSurface;
    const active = state.surfaces[surfaceKey];
    const layout = SURFACE_LAYOUTS[surfaceKey];
    const hasArt = active.artType !== "NONE";

    if (elements.text) {
      elements.text.maxLength = layout.maxChars;
      elements.text.value = active.text;
    }

    if (elements.font) {
      elements.font.value = active.font;
      elements.font.style.fontFamily = FONT_STACKS[active.font] || FONT_STACKS.Arial;
    }

    if (elements.rotation) elements.rotation.value = String(active.rotation);
    if (elements.rotationNumber) elements.rotationNumber.value = String(active.rotation);
    if (elements.fontSize) elements.fontSize.value = String(active.fontSize);

    if (elements.textX) elements.textX.value = String(active.textX);
    if (elements.textY) elements.textY.value = String(active.textY);

    if (elements.artX) elements.artX.value = String(active.artX);
    if (elements.artY) elements.artY.value = String(active.artY);
    if (elements.artScale) elements.artScale.value = String(active.artScale);
    if (elements.artRotation) elements.artRotation.value = String(active.artRotation);
    if (elements.artRotationNumber) elements.artRotationNumber.value = String(active.artRotation);

    if (elements.artX) elements.artX.disabled = !hasArt;
    if (elements.artY) elements.artY.disabled = !hasArt;
    if (elements.artScale) elements.artScale.disabled = !hasArt;
    if (elements.artRotation) elements.artRotation.disabled = !hasArt;
    if (elements.artRotationNumber) elements.artRotationNumber.disabled = !hasArt;

    if (elements.outputs.fontSize) elements.outputs.fontSize.textContent = String(active.fontSize);
    if (elements.outputs.textX) elements.outputs.textX.textContent = String(active.textX);
    if (elements.outputs.textY) elements.outputs.textY.textContent = String(active.textY);
    if (elements.outputs.artX) elements.outputs.artX.textContent = String(active.artX);
    if (elements.outputs.artY) elements.outputs.artY.textContent = String(active.artY);
    if (elements.outputs.artScale) elements.outputs.artScale.textContent = String(active.artScale);

    elements.textStyleButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.textStyle === active.textStyle);
    });

    elements.alignButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.align === active.align);
    });
  }

  function renderArtControls(config, state, elements) {
    const active = getActiveSurfaceState(state);

    elements.artTypeButtons.forEach((button) => {
      button.classList.toggle("is-active", button.dataset.artType === active.artType);
    });

    if (elements.iconPickerWrap) {
      elements.iconPickerWrap.classList.toggle("is-hidden", active.artType !== "ICON");
    }

    renderIconPicker(config, elements, state);

    if (elements.iconPicker) {
      elements.iconPicker.querySelectorAll("[data-icon-id]").forEach((button) => {
        button.classList.toggle("is-selected", button.dataset.iconId === active.artId);
      });
    }
  }

  function renderUploadGroups(config, state, elements) {
    const activeSurface = state.activeSurface;
    const active = getActiveSurfaceState(state);

    elements.uploadGroups.forEach((group) => {
      const isTarget = group.dataset.uploadGroup === activeSurface && active.artType === "UPLOAD";
      group.classList.toggle("is-hidden", !isTarget);
    });

    elements.uploadMeta.forEach((meta) => {
      const surface = state.surfaces[meta.dataset.uploadMeta];
      meta.textContent =
        surface.artType === "UPLOAD" && surface.artName
          ? `${t(config, "uploadCurrentPrefix", "Aktuelles Logo:")} ${surface.artName}`
          : t(config, "uploadNone", "Kein Logo hochgeladen.");
    });
  }

  function renderPreview(config, state, elements) {
    const activeSurfaceKey = state.activeSurface;
    const surfaceConfig = config.surfaces[activeSurfaceKey];
    const active = state.surfaces[activeSurfaceKey];
    const layout = SURFACE_LAYOUTS[activeSurfaceKey];
    const safe = layout.safeBox;
    const clipId = `tkcfg-clip-${activeSurfaceKey}`;

    const baseImageMarkup = surfaceConfig.image
      ? `<image href="${surfaceConfig.image}" x="0" y="0" width="${PREVIEW_WIDTH}" height="${PREVIEW_HEIGHT}" preserveAspectRatio="xMidYMid meet" />`
      : buildFallbackSurface(config);

    const zoneMarkup = `
      <path
        d="${layout.clipPath}"
        fill="rgba(33,174,230,0.16)"
        stroke="rgba(33,174,230,0.95)"
        stroke-width="3"
      />
    `;

    const textMarkup = buildTextMarkup(active, safe);
    const artMarkup = buildArtMarkup(activeSurfaceKey, active, safe, config);

    const markup = `
      <div class="tkcfg__preview-inner">
        <svg
          class="tkcfg__preview-svg"
          viewBox="0 0 ${PREVIEW_WIDTH} ${PREVIEW_HEIGHT}"
          xmlns="http://www.w3.org/2000/svg"
          role="img"
          aria-label="${escapeHtml(`${t(config, "previewAriaPrefix", "Treekey Vorschau")} ${surfaceConfig.label}`)}"
        >
          <defs>
            <clipPath id="${clipId}">
              <path d="${layout.clipPath}" />
            </clipPath>
          </defs>

          ${baseImageMarkup}
          ${zoneMarkup}

          <g clip-path="url(#${clipId})">
            ${artMarkup}
            ${textMarkup}
          </g>
        </svg>

        <div class="tkcfg__preview-badge">${escapeHtml(surfaceConfig.label)}</div>
      </div>
    `;

    elements.previewStages.forEach((stage) => {
      stage.innerHTML = markup;
    });
  }

  function renderSummary(config, state, elements) {
    if (!elements.summary) return;

    const rows = ["front_left", "front_right", "back"].map((surfaceKey) => {
      const label = config.surfaces[surfaceKey].label;
      const summary = buildSurfaceSummary(config, state.surfaces[surfaceKey]);

      return `
        <div class="tkcfg__summary-row">
          <div class="tkcfg__summary-label">${escapeHtml(label)}</div>
          <div class="tkcfg__summary-value">${escapeHtml(summary)}</div>
        </div>
      `;
    });

    elements.summary.innerHTML = rows.join("");
  }

  function syncProperties(config, state, elements) {
    const fl = state.surfaces.front_left;
    const fr = state.surfaces.front_right;
    const bk = state.surfaces.back;

    const frontLeftLabel = config.surfaces.front_left.label;
    const frontRightLabel = config.surfaces.front_right.label;
    const backLabel = config.surfaces.back.label;

    const frontLeftSummary = buildSurfaceSummary(config, fl);
    const frontRightSummary = buildSurfaceSummary(config, fr);
    const backSummary = buildSurfaceSummary(config, bk);

    const publicSummary =
      `${frontLeftLabel}: ${frontLeftSummary} | ${frontRightLabel}: ${frontRightSummary} | ${backLabel}: ${backSummary}`;

    const productionCode = buildProductionCode(state);

    setProp(elements, "summary", publicSummary);
    setProp(elements, "tk-version", TK_VERSION);
    setProp(elements, "tk-layout-profile", TK_LAYOUT_PROFILE);
    setProp(elements, "tk-code", productionCode);

    setSurfaceProps(elements, "fl", fl);
    setSurfaceProps(elements, "fr", fr);
    setSurfaceProps(elements, "bk", bk);
  }

  function setSurfaceProps(elements, prefix, surface) {
    setProp(elements, `${prefix}-text`, surface.text);
    setProp(elements, `${prefix}-font`, surface.font);
    setProp(elements, `${prefix}-style`, surface.textStyle);
    setProp(elements, `${prefix}-size`, surface.fontSize);
    setProp(elements, `${prefix}-rotation`, surface.rotation);
    setProp(elements, `${prefix}-align`, surface.align);
    setProp(elements, `${prefix}-text-x`, surface.textX);
    setProp(elements, `${prefix}-text-y`, surface.textY);
    setProp(elements, `${prefix}-art-type`, surface.artType);
    setProp(elements, `${prefix}-art-id`, surface.artId);
    setProp(elements, `${prefix}-art-name`, surface.artName);
    setProp(elements, `${prefix}-art-x`, surface.artX);
    setProp(elements, `${prefix}-art-y`, surface.artY);
    setProp(elements, `${prefix}-art-scale`, surface.artScale);
    setProp(elements, `${prefix}-art-rotation`, surface.artRotation);
  }

  function setProp(elements, key, value) {
    const input = elements.propInputs[key];
    if (!input) return;
    input.value = value == null ? "" : String(value);
  }

  function buildProductionCode(state) {
    return [
      TK_VERSION,
      buildSurfaceCode("FL", state.surfaces.front_left),
      buildSurfaceCode("FR", state.surfaces.front_right),
      buildSurfaceCode("BK", state.surfaces.back)
    ].join("|");
  }

  function buildSurfaceCode(code, surface) {
    return `${code}[TXT=${sanitizeCodeValue(surface.text)};FNT=${sanitizeCodeValue(surface.font)};STY=${surface.textStyle === "outline" ? "OUT" : "FIL"};SIZ=${surface.fontSize};ROT=${surface.rotation};ALI=${sanitizeCodeValue(surface.align)};TX=${surface.textX};TY=${surface.textY};ART=${surface.artType};AID=${sanitizeCodeValue(surface.artId)};ANM=${sanitizeCodeValue(surface.artName)};AX=${surface.artX};AY=${surface.artY};AS=${surface.artScale};AR=${surface.artRotation}]`;
  }

  function getLocalizedAlign(config, value) {
    if (value === "left") return t(config, "alignLeft", "Links");
    if (value === "right") return t(config, "alignRight", "Rechts");
    return t(config, "alignCenter", "Zentriert");
  }

  function buildSurfaceSummary(config, surface) {
    const parts = [];

    if (surface.text && surface.text.trim() !== "") {
      parts.push(
        `${t(config, "summaryText", "Text")} "${surface.text}" · ${surface.font} · ${
          surface.textStyle === "outline"
            ? t(config, "summaryOutline", "Outline")
            : t(config, "summaryFilled", "Ausgefüllt")
        } · ${surface.fontSize}px · ${getLocalizedAlign(config, surface.align)} · ${surface.rotation}°`
      );
    } else {
      parts.push(t(config, "summaryNoText", "Kein Text"));
    }

    if (surface.artType === "ICON") {
      parts.push(
        surface.artId
          ? `${t(config, "summaryIcon", "Icon")} ${surface.artName || surface.artId} · ${t(config, "summarySize", "Größe")} ${surface.artScale}%`
          : t(config, "summaryNoIconSelected", "Kein Icon gewählt")
      );
    } else if (surface.artType === "UPLOAD") {
      parts.push(
        `${t(config, "summaryLogo", "Logo")} ${surface.artName || t(config, "summaryUploadFallback", "Upload")} · ${t(config, "summarySize", "Größe")} ${surface.artScale}%`
      );
    } else {
      parts.push(t(config, "summaryNoGraphic", "Keine Grafik"));
    }

    parts.push(`${t(config, "summaryTextPos", "Textpos")} ${surface.textX}/${surface.textY}`);

    if (surface.artType !== "NONE") {
      parts.push(`${t(config, "summaryGraphicPos", "Grafikpos")} ${surface.artX}/${surface.artY} · ${surface.artRotation}°`);
    }

    return parts.join(" | ");
  }

  function hasActiveConfiguration(surface) {
    const hasText = Boolean(surface.text && surface.text.trim() !== "");
    const hasArt = surface.artType && surface.artType !== "NONE";
    return hasText || hasArt;
  }

  function isConfiguredTreekey(state) {
    return Object.values(state.surfaces).some((surface) => hasActiveConfiguration(surface));
  }

  function getConfiguredVariantId(root) {
    return String(root.dataset.configuredVariantId || "").trim();
  }

  function getBaseVariantOption(elements, configuredVariantId) {
    if (!elements.variantSelect) return null;

    const options = [...elements.variantSelect.options];

    return (
      options.find((option) => option.value !== configuredVariantId && !option.disabled) ||
      options.find((option) => option.value !== configuredVariantId) ||
      null
    );
  }

  function applyConfiguredVariant(root, config, state, elements) {
    const configuredVariantId = getConfiguredVariantId(root);

    if (!configuredVariantId || !elements.variantSelect) return;

    const options = [...elements.variantSelect.options];
    const configuredOption = options.find((option) => option.value === configuredVariantId) || null;
    const baseOption = getBaseVariantOption(elements, configuredVariantId);

    if (!configuredOption || !baseOption) return;

    const wantsConfigured = isConfiguredTreekey(state);
    const targetOption = wantsConfigured ? configuredOption : baseOption;

    if (elements.variantSelect.value !== targetOption.value) {
      elements.variantSelect.value = targetOption.value;
      elements.variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
    }

    if (elements.submit) {
      const isAvailable = !targetOption.disabled;
      elements.submit.disabled = !isAvailable;
      elements.submit.textContent = isAvailable
        ? t(config, "buttonAddToCart", "Konfigurierten Treekey in den Warenkorb")
        : t(config, "buttonUnavailable", "Aktuell nicht verfügbar");
    }

    if (elements.configPriceNote) {
      if (wantsConfigured) {
        elements.configPriceNote.textContent = !targetOption.disabled
          ? t(config, "configActiveVariant", "Aktive Konfiguration: Variante „Mit Gravur“ wird automatisch verwendet.")
          : t(config, "configActiveVariantUnavailable", "Aktive Konfiguration: Variante „Mit Gravur“ ist aktuell nicht verfügbar.");
      } else {
        elements.configPriceNote.textContent = !targetOption.disabled
          ? t(config, "configBaseVariant", "Ohne Konfiguration: Basisvariante wird verwendet.")
          : t(config, "configBaseVariantUnavailable", "Basisvariante ist aktuell nicht verfügbar.");
      }
    }
  }

  function renderIconPicker(config, elements, state) {
    if (!elements.iconPicker) return;

    const icons = getThemeIconLibrary(config);

    if (!icons.length) {
      elements.iconPicker.innerHTML = `
        <div class="tkcfg__icon-empty">
          ${escapeHtml(t(config, "iconEmpty", "Keine Icons hinterlegt. Füge im Theme Editor unter diesem Abschnitt Icon-Blöcke hinzu."))}
        </div>
      `;
      return;
    }

    const activeArtId = state?.surfaces?.[state.activeSurface]?.artId || "";

    elements.iconPicker.innerHTML = icons
      .map((icon) => {
        const isSelected = icon.id === activeArtId ? " is-selected" : "";
        return `
          <button type="button" class="tkcfg__icon-btn${isSelected}" data-icon-id="${icon.id}" aria-label="${escapeHtml(icon.label)}">
            <span class="tkcfg__icon-preview">
              <img src="${icon.image}" alt="${escapeHtml(icon.label)}" loading="lazy">
            </span>
            <span class="tkcfg__icon-label">${escapeHtml(icon.label)}</span>
            <span class="tkcfg__icon-badge">${escapeHtml(t(config, "iconBadge", "theme"))}</span>
          </button>
        `;
      })
      .join("");
  }

  function buildTextMarkup(surface, safeBox) {
    if (!surface.text || surface.text.trim() === "") return "";

    const x = safeBox.x + (surface.textX / 100) * safeBox.w;
    const y = safeBox.y + (surface.textY / 100) * safeBox.h;

    const anchor =
      surface.align === "left" ? "start" : surface.align === "right" ? "end" : "middle";

    const family = FONT_STACKS[surface.font] || FONT_STACKS.Arial;
    const weight = FONT_WEIGHTS[surface.font] || 600;
    const fontSize = getRenderedFontSize(surface);
    const isOutline = surface.textStyle === "outline";
    const strokeWidth = Math.max(1.5, fontSize * 0.05);

    return `
      <text
        x="${x}"
        y="${y}"
        text-anchor="${anchor}"
        dominant-baseline="middle"
        font-family="${family}"
        font-size="${fontSize}"
        font-weight="${weight}"
        fill="${isOutline ? "none" : "#000000"}"
        stroke="${isOutline ? "#000000" : "none"}"
        stroke-width="${isOutline ? strokeWidth : 0}"
        paint-order="stroke fill"
        opacity="1"
        text-rendering="geometricPrecision"
        transform="rotate(${surface.rotation || 0}, ${x}, ${y})"
        letter-spacing="0.2"
      >
        ${escapeHtml(surface.text)}
      </text>
    `;
  }

  function buildArtMarkup(surfaceKey, surface, safeBox, config) {
    if (surface.artType === "NONE") return "";

    let href = "";

    if (surface.artType === "ICON") {
      const icon = getThemeIconById(surface.artId, config);
      if (!icon) return "";
      href = icon.image;
    } else if (surface.artType === "UPLOAD") {
      if (!surface.artData) return "";
      href = surface.artData;
    } else {
      return "";
    }

    const dims = getArtDimensions(surfaceKey, surface);
    if (!dims) return "";

    const width = dims.width;
    const height = dims.height;

    const x = safeBox.x + (surface.artX / 100) * safeBox.w - width / 2;
    const y = safeBox.y + (surface.artY / 100) * safeBox.h - height / 2;
    const cx = x + width / 2;
    const cy = y + height / 2;

    return `
      <image
        href="${href}"
        x="${x}"
        y="${y}"
        width="${width}"
        height="${height}"
        preserveAspectRatio="xMidYMid meet"
        opacity="1"
        transform="rotate(${surface.artRotation || 0}, ${cx}, ${cy})"
      />
    `;
  }

  function getRenderedFontSize(surface) {
    return Math.max(22, surface.fontSize * 4.4);
  }

  function enforceAllTextBounds(state) {
    Object.keys(state.surfaces).forEach((surfaceKey) => {
      enforceTextBounds(surfaceKey, state.surfaces[surfaceKey]);
    });
  }

  function enforceTextBounds(surfaceKey, surface) {
    surface.textX = clamp(Number(surface.textX) || 0, 0, 100);
    surface.textY = clamp(Number(surface.textY) || 0, -15, 115);

    if (!surface.text || !surface.text.trim()) return;

    const layout = SURFACE_LAYOUTS[surfaceKey];
    if (!layout) return;

    const safe = layout.safeBox;
    const metrics = measureTextBounds(surface);
    if (!metrics) return;

    let currentX = safe.x + (surface.textX / 100) * safe.w;
    let currentY = safe.y + (surface.textY / 100) * safe.h;

    let minX = safe.x - metrics.minX;
    let maxX = safe.x + safe.w - metrics.maxX;

    const verticalOverflow = safe.h * 0.15;
    let minY = safe.y - verticalOverflow - metrics.minY;
    let maxY = safe.y + safe.h + verticalOverflow - metrics.maxY;

    if (minX > maxX) {
      const midX = safe.x + safe.w / 2;
      minX = midX;
      maxX = midX;
    }

    if (minY > maxY) {
      const midY = safe.y + safe.h / 2;
      minY = midY;
      maxY = midY;
    }

    currentX = clamp(currentX, minX, maxX);
    currentY = clamp(currentY, minY, maxY);

    surface.textX = Math.round(((currentX - safe.x) / safe.w) * 100);
    surface.textY = Math.round(((currentY - safe.y) / safe.h) * 100);
  }

  function measureTextBounds(surface) {
    const text = String(surface.text || "");
    if (!text.trim()) return null;

    if (!measureCanvas) {
      measureCanvas = document.createElement("canvas");
    }

    const ctx = measureCanvas.getContext("2d");
    if (!ctx) return null;

    const fontSize = getRenderedFontSize(surface);
    const fontFamily = FONT_STACKS[surface.font] || FONT_STACKS.Arial;
    const fontWeight = FONT_WEIGHTS[surface.font] || 600;

    ctx.font = `${fontWeight} ${fontSize}px ${fontFamily}`;
    const measured = ctx.measureText(text);

    const width = measured.width + text.length * 0.2;
    const height =
      (measured.actualBoundingBoxAscent || fontSize * 0.72) +
      (measured.actualBoundingBoxDescent || fontSize * 0.28);

    const halfH = height / 2;
    let corners;

    if (surface.align === "left") {
      corners = [
        { x: 0, y: -halfH },
        { x: width, y: -halfH },
        { x: width, y: halfH },
        { x: 0, y: halfH }
      ];
    } else if (surface.align === "right") {
      corners = [
        { x: -width, y: -halfH },
        { x: 0, y: -halfH },
        { x: 0, y: halfH },
        { x: -width, y: halfH }
      ];
    } else {
      corners = [
        { x: -width / 2, y: -halfH },
        { x: width / 2, y: -halfH },
        { x: width / 2, y: halfH },
        { x: -width / 2, y: halfH }
      ];
    }

    const angle = ((surface.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const rotated = corners.map((point) => ({
      x: point.x * cos - point.y * sin,
      y: point.x * sin + point.y * cos
    }));

    return {
      minX: Math.min(...rotated.map((p) => p.x)),
      maxX: Math.max(...rotated.map((p) => p.x)),
      minY: Math.min(...rotated.map((p) => p.y)),
      maxY: Math.max(...rotated.map((p) => p.y))
    };
  }

  function enforceAllArtBounds(state) {
    Object.keys(state.surfaces).forEach((surfaceKey) => {
      enforceArtBounds(surfaceKey, state.surfaces[surfaceKey]);
    });
  }

  function enforceArtBounds(surfaceKey, surface) {
    if (!surface || surface.artType === "NONE") return;

    surface.artX = clamp(Number(surface.artX) || 0, 0, 100);
    surface.artY = clamp(Number(surface.artY) || 0, 0, 100);

    const layout = SURFACE_LAYOUTS[surfaceKey];
    if (!layout) return;

    const safe = layout.safeBox;
    const bounds = measureArtBounds(surfaceKey, surface);
    if (!bounds) return;

    let currentX = safe.x + (surface.artX / 100) * safe.w;
    let currentY = safe.y + (surface.artY / 100) * safe.h;

    let minX = safe.x - bounds.minX;
    let maxX = safe.x + safe.w - bounds.maxX;

    const verticalOverflow = safe.h * 0.08;
    let minY = safe.y - verticalOverflow - bounds.minY;
    let maxY = safe.y + safe.h + verticalOverflow - bounds.maxY;

    if (minX > maxX) {
      const midX = safe.x + safe.w / 2;
      minX = midX;
      maxX = midX;
    }

    if (minY > maxY) {
      const midY = safe.y + safe.h / 2;
      minY = midY;
      maxY = midY;
    }

    currentX = clamp(currentX, minX, maxX);
    currentY = clamp(currentY, minY, maxY);

    surface.artX = Math.round(((currentX - safe.x) / safe.w) * 100);
    surface.artY = Math.round(((currentY - safe.y) / safe.h) * 100);
  }

  function measureArtBounds(surfaceKey, surface) {
    if (!surface || surface.artType === "NONE") return null;

    const dims = getArtDimensions(surfaceKey, surface);
    if (!dims) return null;

    const width = dims.width;
    const height = dims.height;

    const halfW = width / 2;
    const halfH = height / 2;

    const corners = [
      { x: -halfW, y: -halfH },
      { x: halfW, y: -halfH },
      { x: halfW, y: halfH },
      { x: -halfW, y: halfH }
    ];

    const angle = ((surface.artRotation || 0) * Math.PI) / 180;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    const rotated = corners.map((point) => ({
      x: point.x * cos - point.y * sin,
      y: point.x * sin + point.y * cos
    }));

    return {
      minX: Math.min(...rotated.map((p) => p.x)),
      maxX: Math.max(...rotated.map((p) => p.x)),
      minY: Math.min(...rotated.map((p) => p.y)),
      maxY: Math.max(...rotated.map((p) => p.y))
    };
  }

  function getArtDimensions(surfaceKey, surface) {
    const layout = SURFACE_LAYOUTS[surfaceKey];
    if (!layout) return null;

    const safe = layout.safeBox;

    const naturalWidth = Math.max(surface.artNaturalWidth || 1, 1);
    const naturalHeight = Math.max(surface.artNaturalHeight || 1, 1);
    const ratio = naturalWidth / naturalHeight;

    const scaleFactor = (Number(surface.artScale) || 80) / 100;

    const height = Math.max(24, safe.h * 0.95 * scaleFactor);
    const width = height * ratio;

    return { width, height };
  }

  function buildFallbackSurface(config) {
    return `
      <rect x="0" y="0" width="${PREVIEW_WIDTH}" height="${PREVIEW_HEIGHT}" fill="#f2f2f2" />
      <text
        x="${PREVIEW_WIDTH / 2}"
        y="${PREVIEW_HEIGHT / 2}"
        text-anchor="middle"
        dominant-baseline="middle"
        fill="#777777"
        font-size="34"
        font-family="Arial, sans-serif"
      >
        ${escapeHtml(t(config, "fallbackPreviewImage", "Bitte Bild in den Section-Einstellungen wählen"))}
      </text>
    `;
  }

  function getActiveSurfaceState(state) {
    return state.surfaces[state.activeSurface];
  }

  function getThemeIconLibrary(config) {
    if (!config || !Array.isArray(config.icons)) return [];

    return config.icons
      .filter((icon) => icon && String(icon.id || "").trim() && String(icon.image || "").trim())
      .map((icon) => ({
        id: String(icon.id).trim(),
        label: String(icon.label || icon.id).trim(),
        image: String(icon.image).trim()
      }));
  }

  function getThemeIconById(id, config) {
    return getThemeIconLibrary(config).find((icon) => icon.id === id) || null;
  }

  function clearSurfaceArt(surface) {
    surface.artType = "NONE";
    surface.artId = "";
    surface.artName = "";
    surface.artData = "";
    surface.artRotation = 0;
    surface.artNaturalWidth = 1;
    surface.artNaturalHeight = 1;
  }

  function clearSurfaceUploadInput(root, surfaceKey) {
    const input = root.querySelector(`[data-upload-input="${surfaceKey}"]`);
    if (input) clearNativeFileInput(input);
  }

  function getImageNaturalSize(src) {
    return new Promise((resolve) => {
      if (!src) {
        resolve({ width: 1, height: 1 });
        return;
      }

      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth || 1,
          height: img.naturalHeight || 1
        });
      };
      img.onerror = () => resolve({ width: 1, height: 1 });
      img.src = src;
    });
  }

  async function hydrateArtNaturalSizes(state, config) {
    const tasks = Object.values(state.surfaces).map(async (surface) => {
      if (surface.artType === "ICON" && surface.artId) {
        const icon = getThemeIconById(surface.artId, config);
        if (icon) {
          const size = await getImageNaturalSize(icon.image);
          surface.artNaturalWidth = size.width;
          surface.artNaturalHeight = size.height;
        }
      }
    });

    await Promise.all(tasks);
  }

  function importTkCode(raw, state) {
    if (!raw.startsWith("TKV")) {
      throw new Error("Ungültige Version");
    }

    const parts = raw.split("|");
    const fl = parseSurfaceCode(parts.find((p) => p.startsWith("FL[")));
    const fr = parseSurfaceCode(parts.find((p) => p.startsWith("FR[")));
    const bk = parseSurfaceCode(parts.find((p) => p.startsWith("BK[")));

    if (!fl || !fr || !bk) throw new Error("Flächen fehlen");

    if (raw.startsWith("TKV2")) {
      applySurfaceCodeV2(state.surfaces.front_left, fl);
      applySurfaceCodeV2(state.surfaces.front_right, fr);
      applySurfaceCodeV2(state.surfaces.back, bk);
    } else {
      applySurfaceCodeV1(state.surfaces.front_left, fl);
      applySurfaceCodeV1(state.surfaces.front_right, fr);
      applySurfaceCodeV1(state.surfaces.back, bk);
    }

    state.activeSurface = "front_left";
    state.activePositionTarget = "text";
  }

  function parseSurfaceCode(part) {
    if (!part) return null;

    const start = part.indexOf("[");
    const end = part.lastIndexOf("]");
    if (start === -1 || end === -1) return null;

    const inner = part.slice(start + 1, end);
    const pairs = inner.split(";");
    const data = {};

    pairs.forEach((pair) => {
      const idx = pair.indexOf("=");
      if (idx === -1) return;
      const key = pair.slice(0, idx).trim();
      const value = pair.slice(idx + 1).trim();
      data[key] = value;
    });

    return data;
  }

  function applySurfaceCodeV2(target, data) {
    target.text = data.TXT || "";
    target.font = data.FNT || "Arial";
    target.textStyle = data.STY === "OUT" ? "outline" : "filled";
    target.fontSize = normalizeNumber(data.SIZ, target.fontSize || 40);
    target.rotation = normalizeRotationValue(data.ROT);
    target.align = data.ALI || "center";
    target.textX = normalizeNumber(data.TX, 50);
    target.textY = normalizeNumber(data.TY, 50);

    target.artType = data.ART || "NONE";
    target.artId = data.AID || "";
    target.artName = data.ANM || "";
    target.artData = "";
    target.artX = normalizeNumber(data.AX, 50);
    target.artY = normalizeNumber(data.AY, 50);
    target.artScale = normalizeNumber(data.AS, 80);
    target.artRotation = normalizeRotationValue(data.AR);
    target.artNaturalWidth = 1;
    target.artNaturalHeight = 1;
  }

  function applySurfaceCodeV1(target, data) {
    target.text = data.TXT || "";
    target.font = data.FNT || "Arial";
    target.textStyle = data.STY === "OUT" ? "outline" : "filled";
    target.fontSize = normalizeNumber(data.SIZ, target.fontSize || 40);
    target.rotation = normalizeRotationValue(data.ROT);
    target.align = data.ALI || "center";
    target.textX = normalizeNumber(data.TX, 50);
    target.textY = normalizeNumber(data.TY, 50);

    if (String(data.LOG || "0") === "1") {
      target.artType = "UPLOAD";
      target.artId = "";
      target.artName = data.LNM || "";
      target.artData = "";
      target.artX = normalizeNumber(data.LX, 50);
      target.artY = normalizeNumber(data.LY, 50);
      target.artScale = normalizeNumber(data.LS, 80);
      target.artRotation = 0;
      target.artNaturalWidth = 1;
      target.artNaturalHeight = 1;
    } else {
      clearSurfaceArt(target);
      target.artX = 50;
      target.artY = 50;
      target.artScale = 80;
    }
  }

  function setAdminStatus(elements, message) {
    if (!elements.admin?.statusEl) return;
    elements.admin.statusEl.textContent = message || "";
  }

  function sanitizeCodeValue(value) {
    return String(value || "")
      .replace(/[|[\]{};]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function normalizeRotationValue(rawValue) {
    let value = Number(rawValue);
    if (Number.isNaN(value)) value = 0;

    value = clamp(Math.round(value), -180, 180);

    const snapTargets = [-180, -135, -90, -45, 0, 45, 90, 135, 180];
    const snapTolerance = 2;

    for (const target of snapTargets) {
      if (Math.abs(value - target) <= snapTolerance) return target;
    }

    return value;
  }

  function preloadConfiguredFonts(config) {
    if (!document.fonts || !Array.isArray(config.fonts)) return Promise.resolve();

    const fontNames = [...new Set(config.fonts.map((font) => font.value).filter(Boolean))];
    return Promise.all(fontNames.map((fontName) => ensureFontLoaded(fontName)));
  }

  async function ensureFontLoaded(fontName) {
    if (!document.fonts || !CUSTOM_FONT_LOADS[fontName]) return;

    try {
      await document.fonts.load(CUSTOM_FONT_LOADS[fontName], "SASCHA 2026");
      await document.fonts.ready;
    } catch (error) {
      console.warn(`Font konnte nicht geladen werden: ${fontName}`, error);
    }
  }

  function isAllowedUploadType(file) {
    const allowed = ["image/svg+xml", "image/png"];
    return allowed.includes(file.type) || /\.(svg|png)$/i.test(file.name);
  }

  function clearNativeFileInput(input) {
    input.value = "";
  }

  function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function normalizeNumber(value, fallback) {
    const num = Number(value);
    return Number.isFinite(num) ? num : fallback;
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-scroll-to]");
    if (!trigger) return;

    const targetSelector = trigger.getAttribute("data-scroll-to");
    if (!targetSelector) return;

    const target = document.querySelector(targetSelector);
    if (!target) return;

    event.preventDefault();

    const stickyHeader =
      document.querySelector("sticky-header") ||
      document.querySelector(".shopify-section-header-sticky") ||
      document.querySelector(".section-header") ||
      document.querySelector("#shopify-section-header");

    const headerHeight = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    const extraOffset = 20;

    const top =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      headerHeight -
      extraOffset;

    window.scrollTo({
      top,
      behavior: "smooth"
    });
  });
})();